import React from "react";
import {
  ChartBar,
  CheckCircle,
  CurrencyCircleDollar,
  PhoneCall,
  ShoppingBagOpen,
  Storefront,
  Truck,
  UsersThree,
} from "@phosphor-icons/react";
import RghtnowMarketingHeader from "./RghtnowMarketingHeader";
import RghtnowMarketingFooter from "./RghtnowMarketingFooter";
import AnimatedStats from "../components/AnimatedStats";
import "./rghtnow-pages.css";

const upload = (path) => `https://rghtnow.com/wp-content/uploads/${path}`;

const heroFeatures = [
  { title: "High Earnings", icon: CurrencyCircleDollar },
  { title: "Daily Payouts", icon: Storefront },
  { title: "Support", icon: PhoneCall },
  { title: "Safe & Secure", icon: CheckCircle },
];

const whyItems = [
  {
    title: "High Earnings",
    text: "Earn more with every delivery",
    icon: ChartBar,
    tone: "orange",
  },
  {
    title: "Daily Payouts",
    text: "Instant payments in your account",
    icon: ShoppingBagOpen,
    tone: "pink",
  },
  {
    title: "Flexible Hours",
    text: "Work on your own schedule",
    icon: CheckCircle,
    tone: "purple",
  },
  {
    title: "Safe & Secure",
    text: "Your safety is our priority",
    icon: Truck,
    tone: "orange",
  },
  {
    title: "Support",
    text: "We're always here to help you",
    icon: PhoneCall,
    tone: "pink",
  },
];

const serviceTypes = [
  "Food Delivery",
  "Grocery Delivery",
  "Parcel Delivery",
  "Taxi / Ride Service",
  "Water Delivery",
  "Pharmacy Delivery",
  "Multi-Service Delivery",
];

const deliveryZones = [
  "Central City",
  "North Zone",
  "South Zone",
  "East Zone",
  "West Zone",
  "Airport Area",
];

const SelectField = ({ label, options, placeholder = "Select" }) => {
  const id = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <label className="rght-field" htmlFor={id}>
      <span>{label}</span>
      <select id={id} defaultValue="">
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
};

const TextField = ({ label, placeholder, type = "text" }) => {
  const id = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <label className="rght-field" htmlFor={id}>
      <span>{label}</span>
      <input id={id} type={type} placeholder={placeholder} />
    </label>
  );
};

const ChoiceGroup = ({ label, options, type = "radio", columns = "auto", wide = false }) => (
  <div className={`rght-field ${wide ? "is-wide" : ""}`}>
    <span>{label}</span>
    <div className={`rght-choice-list rght-choice-list-${columns}`}>
      {options.map((option) => (
        <label key={option}>
          <input name={label} type={type} value={option} />
          <span>{option}</span>
        </label>
      ))}
    </div>
  </div>
);

const BecomeADeliveryPartnerPage = () => (
  <div className="rght-page become-delivery-partner-page">
    <RghtnowMarketingHeader active="delivery" />

    <main>
      <section className="rght-hero rght-partner-hero">
        <div className="container rght-hero-grid">
          <div className="rght-hero-copy">
            <span className="rght-kicker">Partner With RGHT NOW</span>
            <h1>
              Become a Delivery Partner on <em>RGHT NOW</em>
            </h1>
            <p>Deliver smiles. Earn more. Grow with India's fastest growing platform.</p>
            <div className="rght-hero-feature-grid rght-hero-feature-grid-two">
              {heroFeatures.map((item) => {
                const Icon = item.icon;

                return (
                  <div className="rght-hero-feature" key={item.title}>
                    <span>
                      <Icon size={22} weight="fill" />
                    </span>
                    <strong>{item.title}</strong>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="rght-hero-art">
            <img src={upload("2026/06/partner-1024x819.png")} alt="RGHT NOW delivery partner" />
          </div>
        </div>
      </section>

      <section className="rght-section rght-intake-section" id="delivery-partner-form">
        <div className="container rght-intake-grid">
          <form className="rght-form-card rght-intake-card" onSubmit={(event) => event.preventDefault()}>
            <div className="rght-form-title">
              <h2>Business Partner Registration</h2>
              <p>Fill in your details below and our team will get in touch with you</p>
            </div>
            <div className="rght-field-grid">
              <TextField label="Full Name" placeholder="Enter full name" />
              <TextField label="Mobile Number" placeholder="Enter mobile number" type="tel" />
              <TextField label="WhatsApp number" placeholder="Enter whatsApp number" type="tel" />
              <TextField label="Email Address (Optional)" placeholder="Enter email" type="email" />
              <SelectField label="Country" options={["India", "United Arab Emirates", "Saudi Arabia", "Qatar", "Oman", "Kuwait"]} />
              <TextField label="City / Area" placeholder="Enter Area / Location" />
              <SelectField label="Preferred Language" options={["English", "Hindi", "Arabic"]} />
              <ChoiceGroup label="Partner Type" options={["Individual Delivery Partner", "Company / Fleet / Agency"]} columns="stack" />
              <ChoiceGroup label="Checkbox" options={serviceTypes} type="checkbox" columns="two" />
              <SelectField label="Multi Select" options={deliveryZones} placeholder="Multi Select" />
              <ChoiceGroup label="Do You Own Vehicle?" options={["Yes", "No"]} columns="auto" />
              <ChoiceGroup label="Availability" options={["Full-Time", "Part-Time", "Weekends Only"]} columns="auto" />
            </div>
            <button className="rght-button rght-button-pink rght-submit-button" type="submit">
              Submit Details
            </button>
          </form>

          <aside className="rght-why-panel">
            <h2>
              Why Deliver With <em>RGHT NOW?</em>
            </h2>
            <div className="rght-why-list">
              {whyItems.map((item) => {
                const Icon = item.icon;

                return (
                  <article className="rght-why-item" key={item.title}>
                    <span className={`rght-why-icon ${item.tone}`}>
                      <Icon size={28} weight="fill" />
                    </span>
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  </article>
                );
              })}
            </div>
            <div className="rght-sidebar-cta">
              <h3>Ready to Grow Your Business?</h3>
              <p>Drive traffic, generate leads and boost conversions.</p>
              <img src={upload("2026/05/OBJECTS.svg")} alt="" />
            </div>
          </aside>
        </div>
      </section>

      <AnimatedStats subtitle="Delivery partners help RGHT NOW scale faster service coverage across dense city neighborhoods." />

      <section className="rght-seller-strip">
        <div className="container rght-seller-inner">
          <div>
            <h2>Start Selling on RGHT NOW Today!</h2>
            <p>More orders. More customers. More growth.</p>
          </div>
          <div className="rght-store-badges">
            <img src={upload("2023/09/Google_Play_Store_badge_EN.svg-1024x303.png")} alt="Google Play" />
            <img src={upload("2023/09/Download_on_the_App_Store_Badge.svg-1024x304.webp")} alt="App Store" />
          </div>
          <UsersThree size={50} weight="fill" />
        </div>
      </section>
    </main>

    <RghtnowMarketingFooter />
  </div>
);

export default BecomeADeliveryPartnerPage;
